import { trackOptimizelyEvent } from '@/app_lib/optimizely/utils';
import { checkPianoAccountExists } from '@/main/lib/services/Piano/checkPianoAccountExists';
import { isSubscribePage } from '@/main/lib/utils/paywallUtils';
import type { Client } from '@optimizely/optimizely-sdk';
import sha256 from 'crypto-js/sha256';
import type CustomWindow from 'interfaces/Utils';
import findUserInPiano from 'lib/personalizationApi/findUserInPiano';
import passwordlessRegister from 'lib/personalizationApi/passwordlessRegister';
import subscribeUser from 'lib/personalizationApi/subscribeUser';
import geoIp from 'paymentApi/geoIp';
import { OPTIMIZELY_EVENT_VALUES } from 'providers/OptimizelyProvider/utils';
import type { EventData } from 'services/Gtm/entities';
import type { Tinypass } from 'services/Piano/entities/Tinypass';
import { Subscriber } from 'services/Subscriber';
import { getLocalStorageObject, updateLocalStorageObject } from 'utils/localStorageUtils';
import CookieService from 'utils/miscUtils/CookieService';
import type {
  CheckoutCustomEvent,
  ConversionData,
  ConversionResponse,
  CustomEvent,
  ExperienceEvent,
  LoginDataProps,
  OfferParams,
  OfferTerms,
  Params,
  ResponseVariableEvent,
  ResponseVariables,
  TemplateParams,
  Term,
  TermDetailsProps,
} from './entities/PianoCallbackParams';
import { isRegisterFailureResponse, type RegisterResponse } from 'src/pages/api/piano/registerPasswordlessUser';

declare let window: CustomWindow;

interface SessionData {
  checkoutStarted: boolean | undefined;
  currentTerm: Term | undefined;
  offerTermClicked: boolean;
  offerTerms: OfferTerms | undefined;
  termUnset: boolean;
}

const sessionData: SessionData = {
  checkoutStarted: undefined,
  currentTerm: undefined,
  offerTermClicked: false,
  offerTerms: undefined,
  termUnset: true,
};

const unsetTermProps = {
  currency: 'Currency unset',
  description: 'Description unset',
  name: 'Name unset',
  price: 'Price unset',
};

let reloadOnClose: boolean;
let productPayload: {
  displayMode: string;
  experienceActionId: string;
  experienceId: string;
  lts: string;
  offerId: string;
  templateVariantId: string;
  templateId: string;
  terms: string[] | undefined;
};

export const getLts = (tp: Tinypass, experienceId: string, eventType: string): string => {
  const experienceResult = tp.experience._getLastExecutionResult();
  const userSegments = experienceResult.result?.events?.find(
    (event) => event.eventType === eventType && event.eventExecutionContext.experienceId === experienceId,
  )?.eventExecutionContext.userSegments;
  const lts = userSegments?.COMPOSER1X
    ? userSegments?.COMPOSER1X.segments.find((score) => score.startsWith('LTs'))
    : 'no_score';

  return lts || 'no_score';
};

const pianoHandlers = (
  tp: Tinypass,
  updateResponseVariables: (responseVariables: ResponseVariables) => void,
  sendEventOnConsent: (eventData: EventData) => void,
  setConversionData: React.Dispatch<React.SetStateAction<ConversionData | null>>,
  optimizely: Client,
) => ({
  checkoutClose: (event: CheckoutCustomEvent) => {
    const subscribePage = isSubscribePage();
    const params = new URLSearchParams(window.location.search);
    const redirectUrl = params.get('redirect');
    const trackAndRedirect = () => {
      trackOptimizelyEvent(optimizely, 'and_to_subscription_successful', {});
      if (subscribePage) {
        window.location.href = redirectUrl || '/';
      } else {
        window.location.reload();
      }
    };

    switch (event.state) {
      case 'close':
        if (reloadOnClose) {
          if (subscribePage) {
            window.location.href = redirectUrl || '/';
          } else {
            window.location.reload();
          }
        } else {
          sendEventOnConsent({
            eventAction: 'form abandoned',
            eventCategory: 'paywall',
            eventLabel: 'user abandoned the form',
          });
          trackOptimizelyEvent(optimizely, 'paywall_form_abandoned', {});
        }
        break;
      case 'checkoutCompleted':
        trackOptimizelyEvent(optimizely, 'net_new', {});
        trackAndRedirect();
        break;
      case 'alreadyHasAccess':
        trackAndRedirect();
        break;
      default:
        break;
    }
  },
  checkoutComplete: async (conversion: ConversionResponse) => {
    const { chargeAmount, chargeCurrency, paymentId, termId } = conversion;
    setConversionData({ currency: chargeCurrency, price: chargeAmount });
    try {
      const { name, description } = sessionData.currentTerm as Term;

      const orderCompletedPayload = {
        ...productPayload,
        currency: chargeCurrency,
        logged_in: !!tp.pianoId.getUser(),
        products: sessionData.termUnset
          ? [
              {
                currency: chargeCurrency,
                description,
                id: termId,
                name,
                price: chargeAmount,
              },
            ]
          : [sessionData.currentTerm],
        timestamp: Date.now(),
        transaction_id: paymentId,
        value: chargeAmount,
      };
      sendEventOnConsent({
        eventAction: 'payment info entered',
        eventCategory: 'paywall',
        eventLabel: 'user entered their payment info',
        ...orderCompletedPayload,
      });
      trackOptimizelyEvent(optimizely, 'paywall_info_entered', {});
      sendEventOnConsent({
        eventAction: 'order completed',
        eventCategory: 'paywall',
        eventLabel: 'purchase',
        ...orderCompletedPayload,
      });
      trackOptimizelyEvent(optimizely, 'user_subscribed', {});
      // Track subscription success and general subscription tracking
      trackOptimizelyEvent(optimizely, 'and_to_subscription_successful', {});
      trackOptimizelyEvent(optimizely, 'subscription_tracking', {});
      // Track net_new - new subscriber (user wasn't logged in before subscription)
      trackOptimizelyEvent(optimizely, 'net_new', {});
    } catch (error) {
      console.error(error);
    }

    if (tp.user.isUserValid()) {
      const subscriber = new Subscriber();
      await subscriber.init();
    }
  },
  checkoutCustomEvent: (event: CheckoutCustomEvent) => {
    switch (event.eventName) {
      case 'termSelected':
        {
          const { termId } = event.params;
          if (termId) {
            let selectedProduct;
            if (sessionData.offerTerms && sessionData.offerTerms[termId]) {
              selectedProduct = sessionData.offerTerms[termId];
              sessionData.termUnset = false;
            } else {
              sessionData.termUnset = true;
              selectedProduct = {
                ...unsetTermProps,
                id: termId,
              };
            }
            sessionData.offerTermClicked = true;
            sessionData.currentTerm = selectedProduct;
            const productAddedPayload = {
              ...productPayload,
              currency: selectedProduct.currency,
              logged_in: !!tp.pianoId.getUser(),
              products: [selectedProduct],
              timestamp: Date.now(),
              value: selectedProduct.price,
            };

            sendEventOnConsent({
              eventAction: 'product added',
              eventCategory: 'paywall',
              eventLabel: 'product added',
              ...productAddedPayload,
            });
            trackOptimizelyEvent(optimizely, 'user_subscribed', {});
          }
        }
        break;
      case 'term-changed':
        {
          const { termId } = event.params;
          if (termId && termId !== 'no change') {
            let removedProduct;
            if (sessionData.offerTerms && sessionData.offerTerms[termId]) {
              removedProduct = sessionData.offerTerms[termId];
            } else {
              removedProduct = {
                ...unsetTermProps,
                id: termId,
              };
            }
            const productRemovedPayload = {
              ...productPayload,
              currency: removedProduct.currency,
              logged_in: !!tp.pianoId.getUser(),
              products: [removedProduct],
              timestamp: Date.now(),
              value: removedProduct.price,
            };
            sendEventOnConsent({
              eventAction: 'product removed',
              eventCategory: 'paywall',
              eventLabel: 'product removed from cart',
              ...productRemovedPayload,
            });
            trackOptimizelyEvent(optimizely, 'product_removed', {});
          }
        }
        break;
      case 'payment-method-selected':
        if (sessionData.currentTerm) {
          const checkoutStartedPayload = {
            ...productPayload,
            currency: sessionData.currentTerm.currency,
            logged_in: !!tp.pianoId.getUser(),
            products: [sessionData.currentTerm],
            timestamp: Date.now(),
            value: sessionData.currentTerm.price,
          };
          sendEventOnConsent({
            eventAction: 'checkout started',
            eventCategory: 'paywall',
            eventLabel: 'checkout started',
            ...checkoutStartedPayload,
          });
        }
        break;
      default:
        break;
    }
  },
  checkoutSelectTerm: (termDetails: TermDetailsProps) => {
    if (!sessionData.offerTermClicked) {
      const { termId } = termDetails;
      let selectedProduct;
      if (sessionData.offerTerms && sessionData.offerTerms[termId]) {
        selectedProduct = sessionData.offerTerms[termId];
        sessionData.termUnset = false;
      } else {
        sessionData.termUnset = true;
        selectedProduct = {
          ...unsetTermProps,
          id: termId,
        };
      }

      sessionData.currentTerm = selectedProduct;
      const productAddedPayload = {
        ...productPayload,
        currency: selectedProduct.currency,
        logged_in: !!tp.pianoId.getUser(),
        products: [selectedProduct],
        timestamp: Date.now(),
        value: selectedProduct.price,
      };

      sendEventOnConsent({
        eventAction: 'product added',
        eventCategory: 'paywall',
        eventLabel: 'product added to cart',
        ...productAddedPayload,
      });
    } else {
      sessionData.offerTermClicked = false;
    }
  },
  customEvent: async (event: CustomEvent) => {
    let email: string = '';
    let iframeId: string = '';
    let country: string = '';
    let formName: string = '';
    let registrationConsent: string = 'false';
    let consent: boolean = false;
    let templateId: string = '';
    let displayMode: undefined | 'modal' | 'inline';
    if (typeof event.params?.email !== 'undefined' && event.params.email.length > 0) {
      email = event.params.email;
    }
    if (typeof event.params?.country !== 'undefined' && event.params.country.length > 0) {
      country = event.params.country;
    }
    if (typeof event.params?.registrationconsent !== 'undefined' && event.params.registrationconsent.length > 0) {
      registrationConsent = event.params.registrationconsent;
    }
    if (typeof event.params?.formname !== 'undefined' && event.params.formname.length > 0) {
      formName = event.params.formname;
    }
    if (typeof event.params?.consent !== 'undefined' && event.params.consent.length > 0) {
      consent = event.params.consent === 'true' ? true : false;
    }
    if (typeof event.params?.params !== 'undefined') {
      try {
        const params = JSON.parse(event.params.params) as Params;
        iframeId = params.iframeId;
        templateId = params.templateId;
        displayMode = params.displayMode as 'modal' | 'inline';
      } catch (error) {
        console.error(error);
      }
    }
    const iframe = document.getElementById(iframeId) as HTMLIFrameElement;
    let iframeOrigin: string;
    if (iframe) {
      iframeOrigin = new URL(iframe.src).origin;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source') || '';
    const utmMedium = urlParams.get('utm_medium') || '';
    const utmCampaign = urlParams.get('utm_campaign') || '';

    switch (event.eventName) {
      case 'passwordlessClose':
        window.location.reload();
        break;
      case 'passwordlessRegister': {
        const findUser = await findUserInPiano(email);
        if (!findUser) {
          const registerResponse = await passwordlessRegister(email, registrationConsent)
            .then((response) => response)
            .catch((error: Error) => {
              console.error(error);
            });

          if (!registerResponse) {
            return;
          }

          iframe.contentWindow?.postMessage(
            {
              event: 'success message passwordless registration',
              info: 'User was registered successfully!',
            },
            iframeOrigin,
          );

          CookieService.set('__utp', registerResponse.accessToken, {
            expires: registerResponse.expiresIn,
          });

          const trackLogin = async () => {
            try {
              trackOptimizelyEvent(optimizely, 'user_signed_in', {});
              await geoIp(registerResponse.pianoUid, 'registration_wall', registerResponse.accessToken);
              sendEventOnConsent({
                eventAction: 'create account',
                eventCategory: 'account',
                eventLabel: 'account creation successful',
                userIDCD: sha256(email).toString(),
              });
              trackOptimizelyEvent(optimizely, 'user_signed_up', { value: OPTIMIZELY_EVENT_VALUES.signUpValue });
            } catch (error) {
              console.error(error);
            }
          };

          await trackLogin();
          window.location.reload();
        } else {
          iframe.contentWindow?.postMessage(
            {
              event: 'error message passwordless registration',
              info: 'User already exists!',
            },
            iframeOrigin,
          );
        }

        break;
      }
      case 'newsletterSubscribe': {
        const isArticle = Boolean(document.getElementById('article-piano-template-plea-container'));
        if (isArticle) {
          trackOptimizelyEvent(optimizely, 'ceo_daily_subscription_start', {});
        }

        let pianoUid: string | undefined;
        let zeroBounceValidated = false;
        let zeroBounceStatus: string = '';
        let zeroBounceSubStatus: string = '';

        // Checks for logged in user, compares to submitted email, and then checks if a Piano account exists for the submitted email via API if needed
        const data = await checkPianoAccountExists(email, tp);

        // If the user has an existing Piano account, use that pianoUid
        if (data.hasAccount) {
          pianoUid = data.userId;
          // Get zerobounce values
          const validateEmailResponse = await fetch('/api/validateEmail', {
            body: JSON.stringify({ email }),
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
          });
          const validateEmailResponseData = await validateEmailResponse.json();
          if (validateEmailResponseData.success) {
            zeroBounceValidated = true;
            zeroBounceStatus = validateEmailResponseData.status || '';
            zeroBounceSubStatus = validateEmailResponseData.subStatus || '';
          } else {
            iframe.contentWindow?.postMessage(
              {
                event: 'error message newsletter subscribe',
                info: validateEmailResponseData.message,
              },
              iframeOrigin,
            );
            return;
          }
        } else {
          // If the user does not have an existing Piano account, register a new passwordless account
          try {
            // Updated to use an API endpoint that returns the ZeroBounce status and subStatus and success/failure for ZeroBounce
            const response = await fetch(`/api/piano/registerPasswordlessUser/`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                email,
                consent,
              }),
            });

            const registerResponse: RegisterResponse = await response.json();

            if (isRegisterFailureResponse(registerResponse)) {
              if (registerResponse.isZeroBounceError) {
                iframe.contentWindow?.postMessage(
                  {
                    event: 'error message newsletter subscribe',
                    info: registerResponse.message,
                  },
                  iframeOrigin,
                );
                return;
              } else {
                // We continue to silently fail if the registration fails for reasons unrelated to ZeroBounce
                throw new Error(registerResponse.message);
              }
            }

            // Duplicating logic from unpackAndVerifyUserTokenClient, which is in App router.
            // Pages router will be deprecated soon
            const verifyResponse = await fetch('/api/verifyToken', {
              body: JSON.stringify({ token: registerResponse.access_token }),
              headers: {
                'Content-Type': 'application/json',
              },
              method: 'POST',
            });

            if (!verifyResponse.ok) {
              const errorText = await verifyResponse.text();
              throw new Error(`Token verification failed: ${errorText}`);
            }

            const verifyResult = await verifyResponse.json();

            if (verifyResult.error) {
              throw new Error(`Token verification error: ${verifyResult.error}`);
            }

            pianoUid = verifyResult.decoded.sub;
            zeroBounceValidated = true;
            zeroBounceStatus = registerResponse.status || '';
            zeroBounceSubStatus = registerResponse.subStatus || '';

            await geoIp(pianoUid, 'newsletter_template');
          } catch {
            // We continue to silently fail if the token verification fails
          }
        }

        subscribeUser(
          email,
          country,
          [formName],
          '',
          window.location.pathname,
          true,
          consent,
          'piano',
          displayMode,
          templateId,
          utmSource,
          utmMedium,
          utmCampaign,
          pianoUid,
          zeroBounceValidated,
          zeroBounceStatus,
          zeroBounceSubStatus,
        )
          .then(() => {
            // remove minimum height and margin for the reserved space
            const homepageContainer = document.getElementById('hp-piano-template-plea-container')?.parentElement;
            const articleContainer = document.getElementById('article-piano-template-plea-container');
            const container = homepageContainer || articleContainer;
            if (container) {
              // Animate container height and margin down to 0
              container.style.transition = 'min-height 1s ease';
              container.style.minHeight = '0px';
            }

            const isArticle = Boolean(articleContainer);
            if (isArticle) {
              trackOptimizelyEvent(optimizely, 'ceo_daily_subscription_success', {
                value: OPTIMIZELY_EVENT_VALUES.newsletterSubscription,
              });
            }
            iframe.contentWindow?.postMessage(
              {
                event: 'success message newsletter subscribe',
                info: 'Thank you for subscribing.',
              },
              iframeOrigin,
            );
          })
          .catch(() => {
            const isArticle = Boolean(document.getElementById('article-piano-template-plea-container'));
            if (isArticle) {
              trackOptimizelyEvent(optimizely, 'ceo_daily_subscription_failure', {});
            }

            iframe.contentWindow?.postMessage(
              {
                event: 'error message newsletter subscribe',
                info: 'Your subscription could not be registered.',
              },
              iframeOrigin,
            );
          });
        break;
      }
      case 'subscriptionResume':
        try {
          const resumeResponse = await fetch('/api/resumeSubscription', {
            method: 'POST',
          });
          if (resumeResponse.ok) {
            // Send success message to iframe on successful resume, then reload the page after a short delay
            iframe.contentWindow?.postMessage(
              {
                event: 'success message subscription resume',
                info: 'Your subscription has been successfully resumed.',
              },
              iframeOrigin,
            );
            setTimeout(() => {
              window.location.reload();
            }, 2000); // Adjust the delay as needed
          } else {
            // Send error message to iframe on failure
            const resumeResponseData = await resumeResponse.json();
            iframe.contentWindow?.postMessage(
              {
                event: 'error message subscription resume',
                info: resumeResponseData.message || 'Your subscription could not be resumed.',
              },
              iframeOrigin,
            );
          }
        } catch {
          // Send message to iframe on unexpected error
          iframe.contentWindow?.postMessage(
            {
              event: 'error message subscription resume',
              info: 'Your subscription could not be resumed.',
            },
            iframeOrigin,
          );
        }
        break;
      default:
        break;
    }
  },
  experienceExecute: (event: ExperienceEvent) => {
    const { countryCode } = event.result.events[0].eventExecutionContext;
    tp.pianoId.init({ stage: `countryCode-${countryCode}` });
    tp.setCustomVariable('countryCode', countryCode);
  },
  loginSuccess: async (loginData: LoginDataProps) => {
    const { uid: pianoUID, email } = tp.pianoId.getUser();
    const trackLogin = async () => {
      try {
        trackOptimizelyEvent(optimizely, 'user_signed_in', {});

        const trackingData = {
          eventAction: '',
          eventLabel: '',
        };

        if (loginData.registration) {
          await geoIp(pianoUID, loginData.source === 'OFFER' ? 'paywall' : 'sign_in_popup');
          trackingData.eventAction = 'create account';
          trackingData.eventLabel = 'account creation successful';
        } else {
          trackingData.eventAction = 'sign in';
          trackingData.eventLabel = 'sign in successful';
        }

        sendEventOnConsent({
          ...trackingData,
          eventCategory: 'account',
          userIDCD: sha256(email).toString(),
        });
      } catch (error) {
        console.error(error);
      }
    };

    await trackLogin();
    const redirect = CookieService.get('__rdCO');
    if (redirect) {
      CookieService.remove('__rdCO');
      updateLocalStorageObject('checkoutTrackingProps', {
        login_type: 'passwordless',
        login_method: 'piano',
      });
      window.location.href = decodeURIComponent(redirect);
    } else if (loginData.source !== 'OFFER') {
      window.location.reload();
    } else {
      reloadOnClose = true;
    }
  },
  logout: () => {
    updateLocalStorageObject('checkoutTrackingProps', {
      login_method: null,
      login_type: null,
    });
    const domain = window.location.hostname;
    CookieService.remove('uatr');
    CookieService.remove('uat');
    CookieService.remove('__utp', { domain });
    CookieService.remove('pianoUid');
    CookieService.remove('pianoPaused');
    CookieService.remove('pianoAccess');
    window.location.reload();
  },
  manualCreditRedeemed: () => {
    tp.template.close();
    window.location.reload();
  },
  setResponseVariable: ({ responseVariables }: ResponseVariableEvent) => {
    updateResponseVariables(responseVariables);
  },
  showTemplate: async (templateParams: TemplateParams) => {
    const { containerSelector, templateId, templateVariantId, experienceId, experienceActionId, displayMode } =
      templateParams;
    const lts = getLts(tp, experienceId, 'showTemplate');

    // We want to use the exposure props from the most recent showTemplate exposure
    updateLocalStorageObject('checkoutTrackingProps', {
      experience_id: experienceId,
      experience_action_id: experienceActionId,
      template_id: templateId,
      template_variant_id: templateVariantId,
      lts: lts === 'no_score' ? lts : lts?.split(':')[2],
      persistExposureProps: true,
    });

    const productViewedPayload = {
      displayMode,
      templateId,
      templateVariantId,
      experienceId,
      experienceActionId,
    };

    if (displayMode === 'inline') {
      const container = document.querySelector(containerSelector);
      if (containerSelector && container) {
        const observer = new IntersectionObserver(
          (entries, obs) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                sendEventOnConsent({
                  eventAction: 'piano template viewed',
                  eventLabel: 'offer shown to user',
                  eventCategory: 'paywall',
                  ...productViewedPayload,
                });
                // Track paywall exposure to Optimizely
                trackOptimizelyEvent(optimizely, 'paywall_exposures', {});
                if (obs && typeof obs.disconnect === 'function') {
                  obs.disconnect();
                }
              }
            });
          },
          { threshold: 0.5 },
        );
        observer.observe(container);
      }
    } else {
      // If displayMode is modal, then modals are always above the fold, so we can send the viewed event immediately
      sendEventOnConsent({
        eventAction: 'piano template viewed',
        eventLabel: 'offer shown to user',
        eventCategory: 'paywall',
        ...productViewedPayload,
      });
      // Track paywall exposure to Optimizely
      trackOptimizelyEvent(optimizely, 'paywall_exposures', {});
    }

    if (containerSelector === '.paywallax-ii-selector') {
      updateResponseVariables({ showPaywallax: true } as ResponseVariables);
    }

    if (displayMode === 'modal') {
      // Modal template close buttons are technically on the outside of the iframe, so to track close clicks, we need to add a listener here
      window.addEventListener('click', (event) => {
        const eventTarget = event.target as HTMLElement;
        const eventTargetParent = eventTarget.parentElement as HTMLElement;
        if (
          event.target &&
          eventTarget.className.includes('tp-close') &&
          eventTargetParent.className.includes('tp-iframe-wrapper')
        ) {
          const templateTrackingPayload = {
            eventAction: 'newsletter template clicked',
            eventLabel: 'user clicked on newsletter template',
            eventCategory: 'piano template',
            displayMode: displayMode,
            manualContentPlacement: 'piano template',
            manualClickText: (event.target as HTMLElement).textContent,
            manualClickType: 'close',
          };
          sendEventOnConsent(templateTrackingPayload);
        }
      });
    }
  },
  showOffer: async (offerParams: OfferParams) => {
    const { containerSelector, offerId, displayMode, experienceActionId, experienceId, templateId, templateVariantId } =
      offerParams;

    if (containerSelector === '.paywallax-ii-selector') {
      updateResponseVariables({ showPaywallax: true } as ResponseVariables);
    }
    try {
      const response = await fetch(`/api/getPianoData/offerTerms/${offerId}/`);
      let terms;
      let listOfTerms;
      if (response.ok) {
        terms = (await response.json()) as OfferTerms;
        sessionData.offerTerms = terms;
        // @ts-ignore
        listOfTerms = Object.values(terms).map((term: Term) => term.name);
      }

      const lts = getLts(tp, experienceId, 'showOffer');

      productPayload = {
        displayMode,
        experienceActionId,
        experienceId,
        lts: lts === 'no_score' ? lts : lts?.split(':')[2],
        offerId,
        templateId,
        templateVariantId,
        terms: listOfTerms,
      };

      const subscribePage = isSubscribePage();

      if (subscribePage) {
        const currentCheckoutProps = getLocalStorageObject('checkoutTrackingProps');
        // We only want to overwrite exposure props on subscribe pages if there aren't values from a recent,
        // more relevant exposure
        if (!currentCheckoutProps?.persistExposureProps) {
          updateLocalStorageObject('checkoutTrackingProps', {
            experience_id: experienceId,
            experience_action_id: experienceActionId,
            template_id: templateId,
            template_variant_id: templateVariantId,
            offer_id: offerId,
            lts: lts === 'no_score' ? lts : lts?.split(':')[2],
            terms: listOfTerms,
          });
        }
        // We only want to overwrite referral params on subscribe pages if there aren't values from a subscribe page
        // exposure that resulted from navigating from another template (which would have set relevant params)
        // persistReferralParams is set to true when clicking "change subscription" from checkout, and also when the referral params
        // are initially set from a non-subscribe page offer template exposure
        if (!currentCheckoutProps?.persistReferralParams) {
          const queryString = window.location.search;
          updateLocalStorageObject('checkoutTrackingProps', {
            checkout_referral_params: queryString || null,
            persistReferralParams: true,
          });
        }
      } else {
        // For non-subscribe pages, we always want to set the exposure props from the most recent offer template shown
        updateLocalStorageObject('checkoutTrackingProps', {
          experience_id: experienceId,
          experience_action_id: experienceActionId,
          template_id: templateId,
          template_variant_id: templateVariantId,
          offer_id: offerId,
          lts: lts === 'no_score' ? lts : lts?.split(':')[2],
          terms: listOfTerms,
          persistExposureProps: true,
        });
      }

      const productViewedPayload = {
        ...productPayload,
        logged_in: !!tp.pianoId.getUser(),
        products: [
          {
            offer_id: offerId,
          },
        ],
        timestamp: Date.now(),
      };
      sendEventOnConsent({
        eventAction: 'product viewed',
        eventCategory: 'paywall',
        eventLabel: 'offer shown to user',
        ...productViewedPayload,
      });
      // Track paywall exposure to Optimizely
      trackOptimizelyEvent(optimizely, 'paywall_exposures', {});
    } catch (error) {
      console.error(error);
    }
    const iframes = document.querySelectorAll('iframe');
    iframes.forEach((i) => {
      if (i.name.includes('offer')) {
        i.setAttribute('aria-label', 'subscribe offer');
      }
    });
  },
});

export default pianoHandlers;
